/**
 * UI Slice
 *
 * Manages UI state like filters, sorting, expanded rows, and delete confirmations
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SortKey } from '../../../utils/types';

interface UIState {
  searchTerm: string;
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  expandedRecordId: string | null;
  showDeleteConfirm: boolean;
  pendingDeleteId: string | null;
  pendingDeleteFlightNo: string | null;
}

const initialState: UIState = {
  searchTerm: '',
  sortBy: 'startDate',
  sortDir: 'desc',
  expandedRecordId: null,
  showDeleteConfirm: false,
  pendingDeleteId: null,
  pendingDeleteFlightNo: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },

    toggleSort: (state, action: PayloadAction<SortKey>) => {
      if (state.sortBy === action.payload) {
        state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortBy = action.payload;
        state.sortDir = 'asc';
      }
    },

    toggleExpandRecord: (state, action: PayloadAction<string>) => {
      state.expandedRecordId = state.expandedRecordId === action.payload ? null : action.payload;
    },

    openDeleteConfirm: (state, action: PayloadAction<{ id: string; flightNo: string }>) => {
      state.showDeleteConfirm = true;
      state.pendingDeleteId = action.payload.id;
      state.pendingDeleteFlightNo = action.payload.flightNo;
    },

    closeDeleteConfirm: (state) => {
      state.showDeleteConfirm = false;
      state.pendingDeleteId = null;
      state.pendingDeleteFlightNo = null;
    },
  },
});

export const {
  setSearchTerm,
  toggleSort,
  toggleExpandRecord,
  openDeleteConfirm,
  closeDeleteConfirm,
} = uiSlice.actions;

export default uiSlice.reducer;
